/* eslint-disable no-param-reassign */
/* eslint-disable no-loop-func */
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import {
    INCREASE_TO, DECREASE_TO, STAY_ABOVE, STAY_BELOW, ACHIEVED,
    HARD, HARD_SOFT,
    EPSILON,
    CONFIDENCE_LOW, CONFIDENCE_MEDIUM, CONFIDENCE_HIGH,
} from "const"
import { OkrKeyResultUpdate } from "server/models"
import { ConfidenceType } from "client/utils"
import {
    StoreType,
    OkrKeyResultObjectProps,
} from "client/store"
import {
    DateFormatterString,
    IncreaseToIcon,
    DecreaseToIcon,
} from "client/components"
import { Confidence } from "client/pages/okr/styled"
import {
    KeyResultGraphWrapper,
    GraphTitle,

    GraphSuccessSection,
    GraphCriticalSection,
    GraphFailSection,

    GridLine,
    GridHorizontalLineText,
    GridVerticalLineText,

    GraphUpdateLine,
    GraphUpdateLineFilled,
    GraphUpdatePoint,
    GraphAchievedLine,

    GraphCrossLine,
    GraphTargetLine,
    GraphTargetPoint,

    GraphConfidenceLine,
    GraphConfidencePoint,

    GraphConfidenceGradientLow,
    GraphConfidenceGradientMedium,
    GraphConfidenceGradientHigh,

    SoftDeadline,
    SoftDeadlineTooltip,
    HardDeadline,
    HardDeadlineTooltip,

    UpdateTooltip,
    UpdateTooltipTitle,
    UpdateTooltipValueWrapper,
    UpdateTooltipPoint,
    UpdateTooltipValue,
    UpdateTooltipValueDiff,
} from "./styled"

const graphWidth = 550
const updatesGraphHeight = 300
const confidenceGraphHeight = 150

const offsetBetween = 80
const offsetTop = 35
const offsetSide = 20

const graphHeight = offsetTop + updatesGraphHeight + offsetBetween + confidenceGraphHeight

const numberOfConfidenceLines = 5
const confidenceStep = confidenceGraphHeight / numberOfConfidenceLines

const dayTime = 1000 * 60 * 60 * 24

const confidenceHighIndex = 1
const confidenceMediumIndex = 2.5
const confidenceLowIndex = 4

type Text = { x: number, y: number, value: string }
type GridStepX = { days: number, months: number, years: number }
type Point = { id?: number, x: number, y: number }
type ConfidencePoint = { x: number, y: number, confidenceLevel: ConfidenceType }
type Achieved = { id: string, path: string, dateString: string, achieved: boolean, confidenceLevel: ConfidenceType }
type Deadline = { path: string, dateString: string, date: Date }

const KeyResultGraph = ({
    keyResult,
}: OkrKeyResultObjectProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const updateList = useSelector((state: StoreType) => state.okrKeyResultUpdate.updateList)
    const metricList = useSelector((state: StoreType) => state.okrMetric.metricList)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const [updates, setUpdates] = useState<readonly OkrKeyResultUpdate[]>(null)

    const [maxY, setMaxY] = useState<number>(null)
    const [minY, setMinY] = useState<number>(null)
    const [maxX, setMaxX] = useState<Date>(null)
    const [minX, setMinX] = useState<Date>(null)

    const [gridStepY, setGridStepY] = useState<number>(null)
    const [gridStepX, setGridStepX] = useState<GridStepX>(null)
    const [stepY, setStepY] = useState<number>(null)
    const [stepX, setStepX] = useState<number>(null)

    const [titleOffset, setTitleOffset] = useState<number>(null)

    // above/below sections
    const [sectionSuccess, setSectionSuccess] = useState<string>(null)
    const [sectionCritical, setSectionCritical] = useState<string>(null)
    const [sectionFail, setSectionFail] = useState<string>(null)

    const [horizontalGridLines, setHorizontalGridLines] = useState<string[]>([])
    const [verticalGridLines, setVerticalGridLines] = useState<string[]>([])
    const [horizontalLineText, setHorizontalLineText] = useState<Text[]>(null)
    const [verticalLineText, setVerticalLineText] = useState<Text[]>(null)

    const [updateLine, setUpdateLine] = useState<string>(null)
    const [updateLineFilled, setUpdateLineFilled] = useState<string>(null)
    const [updatePoints, setUpdatePoints] = useState<Point[]>(null)

    const [achievedBlocks, setAchievedBlocks] = useState<Achieved[]>(null)

    const [confidenceLine, setConfidenceLine] = useState<string>(null)
    const [confidencePoints, setConfidencePoints] = useState<ConfidencePoint[]>(null)

    const [softDeadline, setSoftDeadline] = useState<Deadline>(null)
    const [hardDeadline, setHardDeadline] = useState<Deadline>(null)
    const [targetLine, setTargetLine] = useState<Point[]>(null)

    useEffect(() => {
        // filter update list
        const krUpdates = updateList[keyResult.id] || []
        const krUpdatesFiltered = krUpdates.filter((update, index) => {
            // if there are several updates on the same date - use the last one on the graph
            const nextUpdate = krUpdates[index + 1]
            if (nextUpdate) {
                const curDate = new Date(update.dateUpdate)
                const nextDate = new Date(nextUpdate.dateUpdate)
                if (
                    curDate.getDate() === nextDate.getDate()
                    && curDate.getMonth() === nextDate.getMonth()
                    && curDate.getFullYear() === nextDate.getFullYear()
                ) return false
                return true
            }
            return true
        })
        setUpdates(krUpdatesFiltered)
    }, [keyResult, updateList])

    useEffect(() => {
        // define max and min Y values
        if ([keyResult, updates].indexOf(null) >= 0) return

        if (keyResult.type === ACHIEVED) {
            const max = 1.5
            const min = -1.5
            setMaxY(max)
            setMinY(min)
            setGridStepY(0.5)
            setStepY(updatesGraphHeight / (max - min))
            setTitleOffset(0)
        } else {
            const critical = keyResult.useCriticalTarget && keyResult.criticalNumber !== null ? keyResult.criticalNumber : null
            const { initialNumber, targetNumber } = keyResult

            let min = critical !== null
                ? Math.min(initialNumber, targetNumber, critical)
                : Math.min(initialNumber, targetNumber)
            let max = critical !== null
                ? Math.max(initialNumber, targetNumber, critical)
                : Math.max(initialNumber, targetNumber)

            // to show success section
            if (keyResult.type === STAY_ABOVE) {
                max += 1
            }
            if (keyResult.type === STAY_BELOW) {
                min -= 1
            }

            updates.forEach((update) => {
                if (update.value < min) min = update.value
                if (update.value > max) max = update.value
            })

            // define step interval for horizontal grid lines
            const interval = max - min
            const milestones: { milestone: number, step: number }[] = [
                { milestone: 12, step: 2 },
                { milestone: 18, step: 3 },
                { milestone: 30, step: 5 },
                { milestone: 60, step: 10 },
            ]

            let step: number = null
            let multiply = 1
            while (step === null) {
                milestones.every((milestone) => {
                    const milestoneMultiplied = milestone.milestone * multiply
                    if (interval <= milestoneMultiplied) {
                        step = milestone.step * multiply
                        return false // break loop
                    }
                    return true
                })
                if (step === null) multiply *= 10
            }

            // round min and max to the closest step
            max = max % step
                ? (max + step) - Math.abs(max % step)
                : max

            min = min % step
                ? (min + step) - Math.abs(min % step) - step
                : min

            setMaxY(max)
            setMinY(min)
            setGridStepY(step)
            setStepY(updatesGraphHeight / (max - min))
            setTitleOffset(multiply.toString().length * 10)
        }
    }, [keyResult, updates])

    useEffect(() => {
        // define max and min X values
        if ([session, updates, titleOffset].indexOf(null) >= 0) return

        let dateStart = new Date(session.dateStart)
        let dateEnd = new Date(session.dateEnd)
        if (updates.length > 0) {
            const firstDateUpdate = new Date(updates[0].dateUpdate)
            const lastDateUpdate = new Date(updates[updates.length - 1].dateUpdate)
            if (firstDateUpdate < dateStart) dateStart = firstDateUpdate
            if (lastDateUpdate > dateEnd) dateEnd = lastDateUpdate
        }

        const dateStartTime = dateStart.getTime()
        const dateEndTime = dateEnd.getTime()
        const sessionDays = Math.round((dateEndTime - dateStartTime) / dayTime)

        // define step interval for vertical grid lines
        let step: GridStepX
        if (sessionDays <= 10) step = { days: 1, months: null, years: null }
        else if (sessionDays <= 20) step = { days: 2, months: null, years: null }
        else if (sessionDays <= 40) step = { days: 4, months: null, years: null }
        else if (sessionDays <= 62) step = { days: 7, months: null, years: null }
        else if (sessionDays <= 186) step = { days: 16, months: null, years: null }
        else if (sessionDays <= 366) step = { days: null, months: 1, years: null }
        else if (sessionDays <= 550) step = { days: null, months: 2, years: null } // 1.5 years
        else if (sessionDays <= 732) step = { days: null, months: 3, years: null } // 2 years
        else if (sessionDays <= 1100) step = { days: null, months: 6, years: null } // 3 years
        else step = { days: null, months: null, years: 1 }

        setMaxX(dateEnd)
        setMinX(dateStart)
        setGridStepX(step)
        setStepX((graphWidth - titleOffset - 2 * offsetSide) / sessionDays)
    }, [session, updates, titleOffset])

    useEffect(() => {
        // set stay above/below sections
        if ([keyResult, maxY, stepY, titleOffset].indexOf(null) >= 0) return

        const x1 = offsetSide + titleOffset
        const x2 = graphWidth - offsetSide
        const critical = keyResult.useCriticalTarget && keyResult.criticalNumber !== null ? keyResult.criticalNumber : null
        const yCritical = (maxY - critical) * stepY + offsetTop
        const yTarget = (maxY - keyResult.targetNumber) * stepY + offsetTop

        const topSection = critical !== null
            ? `
                M ${x1}, ${offsetTop}
                L ${x2}, ${offsetTop}
                L ${x2}, ${yCritical}
                L ${x1}, ${yCritical}
                Z
            `
            : `
                M ${x1}, ${offsetTop}
                L ${x2}, ${offsetTop}
                L ${x2}, ${yTarget}
                L ${x1}, ${yTarget}
                Z
            `
        const middleSection = critical !== null
            ? `
                M ${x1}, ${yCritical}
                L ${x2}, ${yCritical}
                L ${x2}, ${yTarget}
                L ${x1}, ${yTarget}
                Z
            `
            : null
        const bottomSection = critical !== null
            ? `
                M ${x1}, ${yCritical}
                L ${x2}, ${yCritical}
                L ${x2}, ${offsetTop + updatesGraphHeight}
                L ${x1}, ${offsetTop + updatesGraphHeight}
                Z
            `
            : `
                M ${x1}, ${yTarget}
                L ${x2}, ${yTarget}
                L ${x2}, ${offsetTop + updatesGraphHeight}
                L ${x1}, ${offsetTop + updatesGraphHeight}
                Z
            `

        if (keyResult.type === STAY_ABOVE) {
            setSectionSuccess(topSection)
            setSectionCritical(middleSection)
            setSectionFail(bottomSection)
        }
        if (keyResult.type === STAY_BELOW) {
            setSectionFail(topSection)
            setSectionCritical(middleSection)
            setSectionSuccess(bottomSection)
        }
    }, [keyResult, maxY, stepY, titleOffset])

    useEffect(() => {
        // set horizontal grid lines and text for the graph
        if ([keyResult, maxY, minY, gridStepY, stepY, titleOffset].indexOf(null) >= 0) return

        const lines: string[] = []
        const text: Text[] = []

        const x1 = offsetSide + titleOffset
        const x2 = graphWidth - offsetSide

        // updates graph lines
        const interval = maxY - minY
        const numberOfLines = interval / gridStepY
        for (let i = 0; i <= numberOfLines; i++) {
            const y = i * gridStepY * stepY + offsetTop
            lines.push(`M ${x1},${y} L ${x2},${y}`)

            if (i < numberOfLines) { // hide minimum value
                text.push({ x: x1, y, value: (maxY - gridStepY * i).toLocaleString() })
            }
        }

        // confidence graph lines
        for (let i = 0; i <= numberOfConfidenceLines; i++) {
            const y = i * confidenceStep + offsetTop + updatesGraphHeight + offsetBetween
            lines.push(`M ${x1},${y} L ${x2},${y}`)
        }

        setHorizontalGridLines(lines)

        if (keyResult.type === ACHIEVED) {
            // don't show value text for achieved graph
            setHorizontalLineText(null)
        } else {
            setHorizontalLineText(text)
        }
    }, [keyResult, maxY, minY, gridStepY, stepY, titleOffset])

    useEffect(() => {
        // set vertical grid lines and text for the graph
        if ([maxX, minX, gridStepX, stepX, titleOffset].indexOf(null) >= 0) return

        const dates: Date[] = [minX]
        let date = new Date(minX)

        while (date < maxX) {
            if (gridStepX.days) {
                date.setDate(date.getDate() + gridStepX.days)

                const prevDate = dates[dates.length - 1]
                if (prevDate.getMonth() !== date.getMonth()) {
                    date.setDate(1)
                }

                const diff = (date.getDate() - 1) % gridStepX.days
                if (diff) {
                    date.setDate(date.getDate() - diff)
                }
            }
            if (gridStepX.months) {
                date.setDate(1)
                date.setMonth(date.getMonth() + gridStepX.months)

                const prevDate = dates[dates.length - 1]
                if (prevDate.getFullYear() !== date.getFullYear()) {
                    date.setMonth(0)
                }

                const diff = date.getMonth() % gridStepX.months
                if (diff) {
                    date.setMonth(date.getMonth() - diff)
                }
            }
            if (gridStepX.years) {
                date.setDate(1)
                date.setMonth(0)
                date.setFullYear(date.getFullYear() + gridStepX.years)
            }

            if (date > maxX) date = maxX
            dates.push(new Date(date))
        }

        const lines: string[] = []
        const text: Text[] = []

        const minDateTime = minX.getTime()
        const updatesEndY = offsetTop + updatesGraphHeight
        const confidenceStartY = offsetTop + updatesGraphHeight + offsetBetween

        dates.forEach((d) => {
            const dateTime = d.getTime()
            const numberOfDays = Math.round((dateTime - minDateTime) / dayTime)

            // updates graph lines
            const x = numberOfDays * stepX + offsetSide + titleOffset
            lines.push(`M ${x},${offsetTop} L ${x},${updatesEndY}`)

            // date text
            const { dateString } = getDateString(d)
            text.push({ x, y: updatesEndY, value: dateString })

            // confidence graph lines
            lines.push(`M ${x},${confidenceStartY} L ${x},${graphHeight}`)
        })

        const isTooClose = (diff: number) => {
            if (gridStepX.days || (gridStepX.months && gridStepX.months === 1)) {
                if (diff < 40) return true
            } else if (diff < 60) return true
            return false
        }

        // hide too close dates
        const textFiltered: Text[] = []
        for (let index = 0; index < text.length; index++) {
            const lastIndex = text.length - 1
            const nextIndex = index + 1
            const prevText = textFiltered[textFiltered.length - 1]
            const curText = text[index]
            const nextText = text[nextIndex]

            if (index === lastIndex) {
                const xDiff = curText.x - prevText.x
                if (!isTooClose(xDiff)) textFiltered.push(curText)
            } else if (nextText) {
                const xDiff = nextText.x - curText.x
                if (isTooClose(xDiff) && nextIndex !== lastIndex) {
                    textFiltered.push(nextText)
                    index += 1
                } else {
                    textFiltered.push(curText)
                }
            }
        }

        setVerticalGridLines(lines)
        setVerticalLineText(textFiltered)
    }, [maxX, minX, gridStepX, stepX, titleOffset])

    useEffect(() => {
        // set update and confidence points
        if ([keyResult, updates, minX, maxY, stepX, stepY, titleOffset].indexOf(null) >= 0) return

        const minDateTime = minX.getTime()
        const updLine: string[] = []
        const updPoints: Point[] = []
        const confLine: string[] = []
        const confPoints: ConfidencePoint[] = []

        let xPrev = offsetSide + titleOffset
        let yPrev = (maxY - keyResult.initialNumber) * stepY + offsetTop
        updLine.push(`M ${xPrev},${yPrev}`)
        updPoints.push({ id: 0, x: xPrev, y: yPrev })

        updates.forEach((update, index) => {
            // update graph
            const dateUpdateTime = new Date(update.dateUpdate).getTime()
            const numberOfDays = Math.round((dateUpdateTime - minDateTime) / dayTime)
            const x = numberOfDays * stepX + offsetSide + titleOffset
            const xMiddle = xPrev + (x - xPrev) / 2
            const yUpdate = (maxY - update.value) * stepY + offsetTop

            updLine.push(`C ${index === 0 ? xPrev : xMiddle},${yPrev} ${xMiddle},${yUpdate} ${x},${yUpdate}`)
            updPoints.push({ id: update.id, x, y: yUpdate })
            xPrev = x
            yPrev = yUpdate

            // confidence graph
            let confidenceIndex
            if (update.confidenceLevel === CONFIDENCE_HIGH) confidenceIndex = confidenceHighIndex
            if (update.confidenceLevel === CONFIDENCE_MEDIUM) confidenceIndex = confidenceMediumIndex
            if (update.confidenceLevel === CONFIDENCE_LOW) confidenceIndex = confidenceLowIndex
            const yConfidence = confidenceIndex * confidenceStep + offsetTop + updatesGraphHeight + offsetBetween

            confLine.push(`${index === 0 ? "M" : "L"} ${x},${yConfidence}`)
            confPoints.push({ x, y: yConfidence, confidenceLevel: update.confidenceLevel })
        })

        const yZeroValue = maxY * stepY + offsetTop
        const updLineFilled = updLine.slice()
        updLineFilled.push(
            `L ${xPrev},${yZeroValue}`, // from last update to "0" value
            `L ${offsetSide + titleOffset},${yZeroValue}`, // "0" value on graph start
            "Z", // close line
        )

        if (keyResult.type === ACHIEVED) {
            // don't show graph line and points for achieved
            setUpdateLine(null)
            setUpdatePoints(null)
        } else {
            setUpdateLine(updLine.join(" "))
            setUpdatePoints(updPoints)
        }

        if (keyResult.type === INCREASE_TO || keyResult.type === DECREASE_TO) {
            setUpdateLineFilled(updLineFilled.join(" "))
        } else {
            setUpdateLineFilled(null)
        }

        setConfidenceLine(confLine.join(" "))
        setConfidencePoints(confPoints)
    }, [keyResult, updates, minX, maxY, stepX, stepY, titleOffset])

    useEffect(() => {
        // set achieved/notAchieved blocks
        if ([keyResult, updates, minX, maxX, maxY, gridStepX, stepX, stepY, titleOffset].indexOf(null) >= 0) return

        if (keyResult.type === ACHIEVED) {
            const blocks: Achieved[] = []
            const minDateTime = minX.getTime()

            const addBlocks = (
                prevDate: Date,
                nextDate: Date,
                achieved: boolean,
                confidenceLevel: ConfidenceType,
            ) => {
                const achievedValue = achieved ? 1 : -1
                const y1 = maxY * stepY + offsetTop // zero
                const y2 = (maxY - achievedValue) * stepY + offsetTop

                const prevDateTime = prevDate.getTime()
                let numberOfDays = Math.round((prevDateTime - minDateTime) / dayTime)
                if (numberOfDays > 0) numberOfDays -= 0.5 // keep block center on the date

                while (
                    prevDate.getDate() !== nextDate.getDate()
                    || prevDate.getMonth() !== nextDate.getMonth()
                    || prevDate.getFullYear() !== nextDate.getFullYear()
                ) {
                    const dayIncrease = numberOfDays === 0 ? 0.5 : 1 // keep block center on the date
                    const x1 = Math.round(numberOfDays * stepX + offsetSide + titleOffset)
                    const x2 = Math.round((numberOfDays + dayIncrease) * stepX + offsetSide + titleOffset)
                    const path = `
                        M ${x1},${y1}
                        L ${x2},${y1}
                        L ${x2},${y2}
                        L ${x1},${y2}
                        Z
                    `

                    const { id, dateString } = getDateString(prevDate)

                    blocks.push({
                        id,
                        path,
                        dateString,
                        achieved,
                        confidenceLevel,
                    })

                    numberOfDays += dayIncrease
                    prevDate.setDate(prevDate.getDate() + 1)
                }
            }

            updates.forEach((update, index) => {
                if (index === 0) {
                    addBlocks(
                        new Date(minX),
                        new Date(update.dateUpdate),
                        keyResult.initialNumber === 1,
                        null,
                    )
                }

                let nextDate = index < (updates.length - 1) ? new Date(updates[index + 1].dateUpdate) : new Date()
                if (nextDate > maxX) nextDate = new Date(maxX)

                addBlocks(
                    new Date(update.dateUpdate),
                    nextDate,
                    update.value > 0,
                    update.confidenceLevel,
                )
            })

            setAchievedBlocks(blocks)
        } else {
            setAchievedBlocks(null)
        }
    }, [keyResult, updates, minX, maxX, maxY, gridStepX, stepX, stepY, titleOffset])

    useEffect(() => {
        // set deadlines and target line
        if ([keyResult, minX, maxY, stepX, stepY, titleOffset].indexOf(null) >= 0) return

        if (keyResult.type === STAY_ABOVE || keyResult.type === STAY_BELOW) {
            setSoftDeadline(null)
            setHardDeadline(null)
            setTargetLine(null)
        } else {
            const targetLinePoints: Point[] = [{
                x: offsetSide + titleOffset,
                y: (maxY - keyResult.initialNumber) * stepY + offsetTop,
            }]
            const yEndTargetLine = (maxY - keyResult.targetNumber) * stepY + offsetTop

            const minDateTime = minX.getTime()
            const yEndDeadline = offsetTop + updatesGraphHeight

            if (keyResult.deadlineType === HARD_SOFT) {
                const softDeadlineDate = new Date(keyResult.deadlineSoft)
                const softDeadlineTime = softDeadlineDate.getTime()
                const numberOfDays = Math.round((softDeadlineTime - minDateTime) / dayTime)
                const x = numberOfDays * stepX + offsetSide + titleOffset
                const date = DateFormatterString(softDeadlineDate, lang)
                setSoftDeadline({
                    path: `M ${x},${offsetTop} L ${x},${yEndDeadline}`,
                    dateString: `${date.day} ${date.monthShort} ${date.year}`,
                    date: softDeadlineDate,
                })
            }

            if (keyResult.deadlineType === HARD || keyResult.deadlineType === HARD_SOFT) {
                const hardDeadlineDate = new Date(keyResult.deadlineHard)
                const hardDeadlineTime = hardDeadlineDate.getTime()
                const numberOfDays = Math.round((hardDeadlineTime - minDateTime) / dayTime)
                const x = numberOfDays * stepX + offsetSide + titleOffset
                const date = DateFormatterString(hardDeadlineDate, lang)
                setHardDeadline({
                    path: `M ${x},${offsetTop} L ${x},${yEndDeadline}`,
                    dateString: `${date.day} ${date.monthShort} ${date.year}`,
                    date: hardDeadlineDate,
                })

                targetLinePoints.push({
                    x, // hard deadline x
                    y: yEndTargetLine,
                })
            } else {
                targetLinePoints.push({
                    x: graphWidth - offsetSide,
                    y: yEndTargetLine,
                })
            }

            if (keyResult.type === ACHIEVED) {
                setTargetLine(null)
            } else {
                setTargetLine(targetLinePoints)
            }
        }
    }, [keyResult, minX, maxY, stepX, stepY, titleOffset])

    const getDateString = (date: Date) => {
        const { day, monthShort, year } = DateFormatterString(date, lang)
        let id: string
        let dateString: string
        if (gridStepX.days || (gridStepX.months && gridStepX.months === 1)) {
            id = `${day}${monthShort}`
            dateString = `${day} ${monthShort}`
        } else {
            id = `${day}${monthShort}${year.slice(2, 4)}`
            dateString = `${day} ${monthShort} ${year.slice(2, 4)}`
        }
        return { id, dateString }
    }

    const moveDeadlineTooltip = (
        deadlineId: string,
        tooltipId: string,
        pageX: number,
        pageY: number,
    ) => {
        const deadline = document.querySelector(deadlineId) as SVGPathElement
        const tooltip = document.querySelector(tooltipId) as HTMLElement
        if (!deadline || !tooltip) return

        const targetOffset = deadline.getBoundingClientRect()
        const targetOffsetX = targetOffset.left + targetOffset.width / 2
        if (
            Math.abs(pageX - targetOffsetX) <= 5
            && pageY >= targetOffset.top
            && pageY <= targetOffset.top + targetOffset.height
        ) {
            const graphWrapper = document.querySelector(KeyResultGraphWrapper) as HTMLElement
            const graphWrapperOffset = graphWrapper.getBoundingClientRect()
            const offsetY = targetOffset.top - graphWrapperOffset.top + targetOffset.height
            const offsetX = targetOffset.left - graphWrapperOffset.left - tooltip.offsetWidth / 2

            deadline.style.opacity = "1"
            tooltip.style.opacity = "1"
            tooltip.style.transform = `translate(${offsetX}px, ${offsetY}px)`
        } else {
            deadline.style.opacity = "0.6"
            tooltip.style.opacity = "0"
        }
    }

    const hideDeadlineTooltip = (deadlineId: string, tooltipId: string) => {
        const deadline = document.querySelector(deadlineId) as SVGPathElement
        const tooltip = document.querySelector(tooltipId) as HTMLElement
        if (!deadline || !tooltip) return

        deadline.style.opacity = "0.6"
        tooltip.style.opacity = "0"
    }

    const showUpdateTooltip = (selector: string, pageX: number, pageY: number) => {
        hideUpdateTooltips()
        const tooltip = document.querySelector(selector) as HTMLElement
        const graphWrapper = document.querySelector(KeyResultGraphWrapper) as HTMLElement
        const graphWrapperOffset = graphWrapper.getBoundingClientRect()

        const mouseOffset = 25
        const rightOffset = graphWrapperOffset.left + graphWrapperOffset.width - pageX - mouseOffset

        const offsetY = pageY - graphWrapperOffset.top + mouseOffset
        const offsetX = rightOffset < tooltip.offsetWidth
            ? pageX - graphWrapperOffset.left - tooltip.offsetWidth - mouseOffset
            : pageX - graphWrapperOffset.left + mouseOffset

        tooltip.style.opacity = "1"
        tooltip.style.top = `${offsetY}px`
        tooltip.style.left = `${offsetX}px`
    }

    const hideUpdateTooltips = () => {
        const updateTooltips = document.querySelectorAll("#updateTooltips > div")
        updateTooltips.forEach((tooltip: HTMLElement) => {
            tooltip.style.opacity = "0"
        })
    }

    const moveCrosslines = (closestX: number, pageX: number, pageY: number) => {
        const svg = document.querySelector("svg")
        const svgOffset = svg.getBoundingClientRect()

        const svgPoint = svg.createSVGPoint()
        svgPoint.x = closestX !== null ? closestX : (pageX - svgOffset.left)
        svgPoint.y = pageY - svgOffset.top
        svgPoint.matrixTransform(svg.getScreenCTM().inverse())

        const horizontal = document.querySelector("#horizontalCrossLine") as HTMLElement
        const vertical1 = document.querySelector("#verticalCrossLine1") as HTMLElement
        const vertical2 = document.querySelector("#verticalCrossLine2") as HTMLElement

        horizontal.setAttribute("y1", svgPoint.y.toString())
        horizontal.setAttribute("y2", svgPoint.y.toString())
        horizontal.style.opacity = "1"

        vertical1.setAttribute("x1", svgPoint.x.toString())
        vertical1.setAttribute("x2", svgPoint.x.toString())
        vertical1.style.opacity = "1"

        vertical2.setAttribute("x1", svgPoint.x.toString())
        vertical2.setAttribute("x2", svgPoint.x.toString())
        vertical2.style.opacity = "1"
    }

    const hideCrosslines = () => {
        const horizontal = document.querySelector("#horizontalCrossLine") as HTMLElement
        const vertical1 = document.querySelector("#verticalCrossLine1") as HTMLElement
        const vertical2 = document.querySelector("#verticalCrossLine2") as HTMLElement

        horizontal.style.opacity = "0"
        vertical1.style.opacity = "0"
        vertical2.style.opacity = "0"
    }

    const onMouseMove = (e: React.MouseEvent<SVGPathElement, MouseEvent>) => {
        let closest: HTMLElement = null
        let closestX: number = null

        if (keyResult.type !== ACHIEVED) {
            // show/hide points
            const points = document.querySelectorAll("#updatePoints circle")
            let minDiff = 1000
            points.forEach((point: HTMLElement) => {
                const pointOffset = point.getBoundingClientRect()
                const pointOffsetX = pointOffset.left + pointOffset.width / 2
                const diff = Math.abs(e.pageX - pointOffsetX)
                if (diff < minDiff) {
                    minDiff = diff
                    closest = point as HTMLElement
                }
                point.style.opacity = "0"
            })

            if (closest) {
                closest.style.opacity = "1"
                closestX = parseFloat(closest.getAttribute("cx"))
            }
        } else {
            // get closest block
            const svg = document.querySelector("svg")
            const svgOffset = svg.getBoundingClientRect()

            const svgPoint = svg.createSVGPoint()
            svgPoint.x = e.pageX - svgOffset.left
            svgPoint.matrixTransform(svg.getScreenCTM().inverse())

            const numberOfDays = Math.round((svgPoint.x - offsetSide - titleOffset) / stepX)
            const blockDate = new Date(minX)
            blockDate.setDate(blockDate.getDate() + numberOfDays)

            const blockId = getDateString(blockDate).id
            const block = document.querySelector(`#block_${blockId}`)

            if (block) {
                closest = block as HTMLElement
                const blockOffset = block.getBoundingClientRect()
                closestX = blockOffset.left + blockOffset.width / 2 - svgOffset.left
            }
        }

        if (closest) {
            const closestId = closest.getAttribute("id")
            const updateId = closestId.split("_")[1]
            showUpdateTooltip(`#update_${updateId}`, e.pageX, e.pageY)
        } else {
            hideUpdateTooltips()
        }

        moveCrosslines(closestX, e.pageX, e.pageY)
        moveDeadlineTooltip("#softDeadline", "#softDeadlineTooltip", e.pageX, e.pageY)
        moveDeadlineTooltip("#hardDeadline", "#hardDeadlineTooltip", e.pageX, e.pageY)
    }

    const onMouseLeave = () => {
        if (keyResult.type !== ACHIEVED) {
            // hide points
            const points = document.querySelectorAll("#updatePoints circle")
            points.forEach((point: HTMLElement) => {
                point.style.opacity = "0"
            })
        }

        hideCrosslines()
        hideUpdateTooltips()
        hideDeadlineTooltip("#softDeadline", "#softDeadlineTooltip")
        hideDeadlineTooltip("#hardDeadline", "#hardDeadlineTooltip")
    }

    const metric = metricList[keyResult.metricId] || null
    const metricBefore = metric && !metric.displayAfter ? metric.symbol : ""
    const metricAfter = metric && metric.displayAfter ? metric.symbol : ""

    const sessionDateStart = DateFormatterString(session.dateStart, lang)
    const sessionDateEnd = DateFormatterString(session.dateEnd, lang)

    return (
        <KeyResultGraphWrapper>
            <GraphTitle topOffset={15}>
                {
                    (keyResult.type === INCREASE_TO || keyResult.type === STAY_ABOVE)
                    && <IncreaseToIcon />
                }
                {
                    (keyResult.type === DECREASE_TO || keyResult.type === STAY_BELOW)
                    && <DecreaseToIcon />
                }
                { keyResult.type === INCREASE_TO && lang.shouldIncreaseTo }
                { keyResult.type === DECREASE_TO && lang.shouldDecreaseTo }
                { keyResult.type === STAY_ABOVE && lang.shouldStayAbove }
                { keyResult.type === STAY_BELOW && lang.shouldStayBelow }
                { keyResult.type === ACHIEVED && lang.achievedOrNot }
            </GraphTitle>
            <GraphTitle topOffset={offsetTop + updatesGraphHeight + offsetBetween - 20}>
                {lang.confidenceLevel}
            </GraphTitle>

            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={graphWidth}
                height={graphHeight}
            >
                <g id="aboveBelowSections">
                    {
                        sectionSuccess
                        && <GraphSuccessSection d={sectionSuccess} />
                    }
                    {
                        sectionCritical
                        && <GraphCriticalSection d={sectionCritical} />
                    }
                    {
                        sectionFail
                        && <GraphFailSection d={sectionFail} />
                    }
                </g>
                <g id="gridLines">
                    {
                        horizontalGridLines.map((path) => (
                            <GridLine key={path} d={path} />
                        ))
                    }
                    {
                        verticalGridLines.map((path) => (
                            <GridLine key={path} d={path} />
                        ))
                    }
                </g>
                <g id="gridText">
                    {
                        horizontalLineText && horizontalLineText.map((text) => (
                            <GridHorizontalLineText
                                key={text.value}
                                x={text.x}
                                y={text.y}
                            >
                                {text.value}
                            </GridHorizontalLineText>
                        ))
                    }
                    {
                        verticalLineText && verticalLineText.map((text) => (
                            <GridVerticalLineText
                                key={text.value}
                                x={text.x}
                                y={text.y}
                            >
                                {text.value}
                            </GridVerticalLineText>
                        ))
                    }
                </g>
                <g id="targetLine">
                    {
                        targetLine && (
                            <GraphTargetLine
                                x1={targetLine[0].x}
                                y1={targetLine[0].y}
                                x2={targetLine[1].x}
                                y2={targetLine[1].y}
                            />
                        )
                    }
                </g>
                <g id="updateGraph">
                    {
                        updateLine
                        && <GraphUpdateLine d={updateLine} />
                    }
                    {
                        updateLineFilled
                        && <GraphUpdateLineFilled d={updateLineFilled} />
                    }
                </g>
                <g id="targetLinePoints">
                    {
                        targetLine && ([
                            <GraphTargetPoint
                                key="targetLinePoint1"
                                cx={targetLine[0].x}
                                cy={targetLine[0].y}
                                r="4"
                            />,
                            <GraphTargetPoint
                                key="targetLinePoint2"
                                cx={targetLine[1].x}
                                cy={targetLine[1].y}
                                r="4"
                            />,
                        ])
                    }
                </g>
                <g id="updatePoints">
                    {
                        updatePoints && updatePoints.map((point) => (
                            <GraphUpdatePoint
                                key={point.id}
                                id={`point_${point.id}`}
                                cx={point.x}
                                cy={point.y}
                                r="4"
                            />
                        ))
                    }
                    {
                        updates && updates.length === 0 && targetLine && (
                            <GraphUpdatePoint
                                id="point_target"
                                cx={targetLine[1].x}
                                cy={targetLine[1].y}
                                r="4"
                            />
                        )
                    }
                </g>
                <g id="achievedBlocks">
                    {
                        achievedBlocks && achievedBlocks.map((block) => (
                            <GraphAchievedLine
                                key={block.id}
                                id={`block_${block.id}`}
                                d={block.path}
                                achieved={block.achieved}
                            />
                        ))
                    }
                </g>
                <g id="deadlines">
                    {
                        softDeadline
                        && <SoftDeadline id="softDeadline" d={softDeadline.path} />
                    }
                    {
                        hardDeadline
                        && <HardDeadline id="hardDeadline" d={hardDeadline.path} />
                    }
                </g>
                <g id="confidenceGraph">
                    {
                        confidenceLine
                        && <GraphConfidenceLine d={confidenceLine} stroke="url(#confidenceGradient)" />
                    }
                </g>
                <g id="confidencePoints">
                    {
                        confidencePoints && confidencePoints.map((point) => (
                            <GraphConfidencePoint
                                key={`${point.x}_${point.y}`}
                                cx={point.x}
                                cy={point.y}
                                r="4"
                                confidenceLevel={point.confidenceLevel}
                            />
                        ))
                    }
                </g>
                <g id="crossLines">
                    <GraphCrossLine
                        id="horizontalCrossLine"
                        x1={offsetSide + titleOffset}
                        y1="0"
                        x2={graphWidth - offsetSide}
                        y2="0"
                    />
                    <GraphCrossLine
                        id="verticalCrossLine1"
                        x1="0"
                        y1={offsetTop}
                        x2="0"
                        y2={offsetTop + updatesGraphHeight}
                    />
                    <GraphCrossLine
                        id="verticalCrossLine2"
                        x1="0"
                        y1={offsetTop + updatesGraphHeight + offsetBetween}
                        x2="0"
                        y2={graphHeight}
                    />
                </g>
                <g id="actionWrappers">
                    <path
                        d={`
                            M ${offsetSide + titleOffset},${offsetTop}
                            L ${graphWidth - offsetSide},${offsetTop}
                            L ${graphWidth - offsetSide},${offsetTop + updatesGraphHeight}
                            L ${offsetSide + titleOffset},${offsetTop + updatesGraphHeight}
                            Z
                        `}
                        fill="transparent"
                        onMouseMove={onMouseMove}
                        onMouseLeave={onMouseLeave}
                    />
                    <path
                        d={`
                            M ${offsetSide + titleOffset},${offsetTop + updatesGraphHeight + offsetBetween}
                            L ${graphWidth - offsetSide},${offsetTop + updatesGraphHeight + offsetBetween}
                            L ${graphWidth - offsetSide},${graphHeight}
                            L ${offsetSide + titleOffset},${graphHeight}
                            Z
                        `}
                        fill="transparent"
                        onMouseMove={onMouseMove}
                        onMouseLeave={onMouseLeave}
                    />
                </g>

                <defs>
                    <linearGradient
                        id="confidenceGradient"
                        gradientUnits="userSpaceOnUse"
                        x1="0"
                        x2="0"
                        y1={confidenceHighIndex * confidenceStep + offsetTop + updatesGraphHeight + offsetBetween}
                        y2={confidenceLowIndex * confidenceStep + offsetTop + updatesGraphHeight + offsetBetween}
                    >
                        <GraphConfidenceGradientHigh offset="0%" />
                        <GraphConfidenceGradientMedium offset="50%" />
                        <GraphConfidenceGradientLow offset="100%" />
                    </linearGradient>
                </defs>
            </svg>

            {
                keyResult.type !== ACHIEVED
                && (
                    <div id="updateTooltips">
                        <UpdateTooltip id="update_0">
                            <UpdateTooltipTitle>
                                {`${sessionDateStart.day} ${sessionDateStart.monthShort} ${sessionDateStart.year}`}
                            </UpdateTooltipTitle>
                            <UpdateTooltipValueWrapper>
                                <UpdateTooltipPoint typePoint="update" />
                                <UpdateTooltipValue>
                                    {`${metricBefore} ${keyResult.initialNumber} ${metricAfter}`}
                                </UpdateTooltipValue>
                            </UpdateTooltipValueWrapper>
                            {
                                updates && updates.length > 0 && (
                                    <Confidence confidenceLevel={updates[0].confidenceLevel}>
                                        {lang[updates[0].confidenceLevel]}
                                    </Confidence>
                                )
                            }

                            <UpdateTooltipTitle>{lang.initial}</UpdateTooltipTitle>
                            <UpdateTooltipValueWrapper>
                                <UpdateTooltipPoint typePoint="target" />
                                <UpdateTooltipValue>
                                    {`${metricBefore} ${keyResult.initialNumber} ${metricAfter}`}
                                </UpdateTooltipValue>
                            </UpdateTooltipValueWrapper>
                        </UpdateTooltip>

                        {
                            updates && updates.map((update) => {
                                const dateUpdate = new Date(update.dateUpdate)
                                const date = DateFormatterString(dateUpdate, lang)
                                const targetDiff = (update.value * EPSILON - keyResult.targetNumber * EPSILON) / EPSILON

                                const earlierHardDeadline = hardDeadline && hardDeadline.date > dateUpdate
                                const equalHardDeadline = hardDeadline
                                    && hardDeadline.date.getDate() === dateUpdate.getDate()
                                    && hardDeadline.date.getMonth() === dateUpdate.getMonth()
                                    && hardDeadline.date.getFullYear() === dateUpdate.getFullYear()
                                return (
                                    <UpdateTooltip key={update.id} id={`update_${update.id}`}>
                                        <UpdateTooltipTitle>
                                            {`${date.day} ${date.monthShort} ${date.year}`}
                                        </UpdateTooltipTitle>
                                        <UpdateTooltipValueWrapper>
                                            <UpdateTooltipPoint typePoint="update" />
                                            <UpdateTooltipValue>
                                                {`${metricBefore} ${update.value} ${metricAfter}`}
                                            </UpdateTooltipValue>
                                            <UpdateTooltipValueDiff value={update.diffProgress}>
                                                {`${update.diffProgress > 0 ? "+" : ""}${update.diffProgress}%`}
                                            </UpdateTooltipValueDiff>
                                        </UpdateTooltipValueWrapper>
                                        <Confidence confidenceLevel={update.confidenceLevel}>
                                            {lang[update.confidenceLevel]}
                                        </Confidence>

                                        {
                                            (earlierHardDeadline || equalHardDeadline) && ([
                                                <UpdateTooltipTitle key="title">{lang.target}</UpdateTooltipTitle>,
                                                <UpdateTooltipValueWrapper key="value">
                                                    <UpdateTooltipPoint typePoint="target" />
                                                    <UpdateTooltipValue>
                                                        {`${metricBefore} ${keyResult.targetNumber} ${metricAfter}`}
                                                    </UpdateTooltipValue>
                                                    {
                                                        !equalHardDeadline && (
                                                            <UpdateTooltipValueDiff value={targetDiff}>
                                                                {targetDiff}
                                                            </UpdateTooltipValueDiff>
                                                        )
                                                    }
                                                </UpdateTooltipValueWrapper>,
                                            ])
                                        }
                                    </UpdateTooltip>
                                )
                            })
                        }

                        {
                            updates && updates.length === 0 && (
                                <UpdateTooltip id="update_target">
                                    <UpdateTooltipTitle>
                                        {
                                            hardDeadline
                                                ? hardDeadline.dateString
                                                : `${sessionDateEnd.day} ${sessionDateEnd.monthShort} ${sessionDateEnd.year}`
                                        }
                                    </UpdateTooltipTitle>
                                    <UpdateTooltipTitle>{lang.target}</UpdateTooltipTitle>
                                    <UpdateTooltipValueWrapper>
                                        <UpdateTooltipPoint typePoint="target" />
                                        <UpdateTooltipValue>
                                            {`${metricBefore} ${keyResult.targetNumber} ${metricAfter}`}
                                        </UpdateTooltipValue>
                                    </UpdateTooltipValueWrapper>
                                </UpdateTooltip>
                            )
                        }
                    </div>
                )
            }
            {
                keyResult.type === ACHIEVED
                && achievedBlocks
                && (
                    <div id="updateTooltips">
                        {
                            achievedBlocks.map((block) => (
                                <UpdateTooltip key={block.id} id={`update_${block.id}`}>
                                    <UpdateTooltipTitle>{block.dateString}</UpdateTooltipTitle>
                                    <UpdateTooltipValueWrapper>
                                        <UpdateTooltipPoint typePoint={block.achieved ? "achieved" : "notAchieved"} />
                                        <UpdateTooltipValue>{block.achieved ? lang.achieved : lang.notAchieved}</UpdateTooltipValue>
                                    </UpdateTooltipValueWrapper>
                                    {
                                        block.confidenceLevel && (
                                            <Confidence confidenceLevel={block.confidenceLevel}>
                                                {lang[block.confidenceLevel]}
                                            </Confidence>
                                        )
                                    }
                                </UpdateTooltip>
                            ))
                        }
                    </div>
                )
            }
            {
                softDeadline && (
                    <SoftDeadlineTooltip id="softDeadlineTooltip">
                        <span>{lang.softDeadline}</span>
                        <span>{softDeadline.dateString}</span>
                    </SoftDeadlineTooltip>
                )
            }
            {
                hardDeadline && (
                    <HardDeadlineTooltip id="hardDeadlineTooltip">
                        <span>{lang.hardDeadline}</span>
                        <span>{hardDeadline.dateString}</span>
                    </HardDeadlineTooltip>
                )
            }
        </KeyResultGraphWrapper>
    )
}
export default KeyResultGraph
